import type { Place, RouteSuggestion } from '../types';
import { getPlaceDetails } from '../services/routexApi';
import { placesFromRouteSuggestion } from './routeSuggestionPlaces';

/** Session cache so chat close/reopen and duplicate stops don’t re-hit place-details. */
const urlByStopKey = new Map<string, string>();
const inflightByStopKey = new Map<string, Promise<string | null>>();

export function stopPhotoCacheKey(place: Place): string {
  if (place.place_id && !place.place_id.startsWith('stop-')) {
    return `pid:${place.place_id}`;
  }
  const { lat, lng } = place.location;
  const n = (place.name || '').trim().slice(0, 80);
  return `loc:${n}|${lat.toFixed(4)}|${lng.toFixed(4)}`;
}

function photoUrlFromPlacePhotos(place: Place, apiKey: string | null): string | null {
  const ref = place.photos?.[0]?.photo_reference;
  if (!ref || !apiKey) return null;
  return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=480&photo_reference=${encodeURIComponent(
    ref,
  )}&key=${encodeURIComponent(apiKey)}`;
}

function isResolvablePlaceId(placeId: string | undefined): boolean {
  if (!placeId || placeId.startsWith('stop-')) return false;
  return true;
}

async function fetchBestPhotoUrlFromDetails(place: Place): Promise<string | null> {
  try {
    const details = await getPlaceDetails({
      place_id: isResolvablePlaceId(place.place_id) ? place.place_id : undefined,
      name: place.name,
      location: place.location,
    });
    const list = details.photos;
    if (!list?.length) return null;
    return list[0] ?? null;
  } catch {
    return null;
  }
}

/** Synchronous: memory cache or construct from embedded photo_reference. */
export function peekStopPhotoUrl(place: Place, apiKey: string | null): string | null {
  const ck = stopPhotoCacheKey(place);
  const hit = urlByStopKey.get(ck);
  if (hit) return hit;
  const fromRef = photoUrlFromPlacePhotos(place, apiKey);
  if (fromRef) {
    urlByStopKey.set(ck, fromRef);
    return fromRef;
  }
  return null;
}

/**
 * Returns cached URL, embedded ref URL, or fetches place-details once per stop (deduped in-flight).
 */
export function loadStopPhotoUrl(place: Place, apiKey: string | null): Promise<string | null> {
  const ck = stopPhotoCacheKey(place);
  const existing = urlByStopKey.get(ck);
  if (existing) return Promise.resolve(existing);

  const fromRef = photoUrlFromPlacePhotos(place, apiKey);
  if (fromRef) {
    urlByStopKey.set(ck, fromRef);
    return Promise.resolve(fromRef);
  }

  let p = inflightByStopKey.get(ck);
  if (!p) {
    p = fetchBestPhotoUrlFromDetails(place).then((url) => {
      inflightByStopKey.delete(ck);
      if (url) urlByStopKey.set(ck, url);
      return url;
    });
    inflightByStopKey.set(ck, p);
  }
  return p;
}

/** Fire-and-forget: prefetch hero photos while the assistant message renders (deduped via cache). */
export function warmChatStopPhotosForSuggestions(
  suggestions: RouteSuggestion[],
  apiKey: string | null,
): void {
  for (const s of suggestions) {
    for (const p of placesFromRouteSuggestion(s)) {
      if (!peekStopPhotoUrl(p, apiKey)) void loadStopPhotoUrl(p, apiKey);
    }
  }
}
